import React, { useCallback, useEffect, useState } from "react";
import { patienttitles } from "../../../../utils/title";
import baseUrl from "../../../../api/baseUrl";
import { setPurpose } from "../../../../redux/appointment/appointmentAction";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import socket from "../../../../api/socket";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneInput from "react-phone-number-input";
import WcIcon from "@mui/icons-material/Wc";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { showSmsResultToast, notifySmsBalanceChanged } from "../../../../utils/sms/smsToast";
import PromotionalSmsOptIn from "../../../../components/sms/PromotionalSmsOptIn";
import {
  UNVERIFIED_BOOKING_HINT,
  UNVERIFIED_BOOKING_STYLE,
  isUnverifiedBooking,
} from "../../viewappointment/components/appointmentViewShared";

// Convert 12-hour format (10:00 AM) to 24-hour format (10:00)
const convert12To24Hour = (time12: string): string => {
  if (!time12) return "";
  
  // Remove any extra spaces and split
  const time = time12.trim();
  const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  
  if (!match) {
    // If already in 24-hour format, return as is
    if (time.match(/^\d{2}:\d{2}$/)) {
      return time;
    }
    return "";
  }
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const ampm = match[3].toUpperCase();
  
  if (ampm === "PM" && hours !== 12) {
    hours += 12;
  } else if (ampm === "AM" && hours === 12) {
    hours = 0;
  }
  
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

function OnlineAppointmentModal({
  onlineAppointment,
  setOnlineAppointmentModal,
  doctor,
  onsave,
}: any) {
  const dispatch = useDispatch();
  const purposes = useSelector((state: RootState) => state.appointment);
  const [error, seterror] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [searchPatients, setSearchPatients] = useState<any[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  const [formData, setformData] = useState({
    title: onlineAppointment?.title || "",
    fullName: onlineAppointment?.patientName || "",
    gender: "",
    mobileNo: onlineAppointment?.mobileNo || "",
    dob: "",
    smsPromotionalOptIn: true,
    address: "",
    doctorId: onlineAppointment?.doctorId || "",
    date: onlineAppointment?.date || "",
    startTime: onlineAppointment?.startTime || "",
    endTime: onlineAppointment?.endTime || "",
    interval: onlineAppointment?.interval || "5",
    purpose: "",
    activeTime: onlineAppointment?.activeTime || "",
    patientId: "",
  });

  // Update form data when onlineAppointment changes
  useEffect(() => {
    if (onlineAppointment) {
        
      // Convert 12-hour format to 24-hour format for time inputs
      const startTime24 = convert12To24Hour(onlineAppointment?.startTime || "");
      const endTime24 = convert12To24Hour(onlineAppointment?.endTime || "");
        
      setformData({
        title: onlineAppointment?.title || "",
        fullName: onlineAppointment?.patientName || "",
        gender: "",
        mobileNo: onlineAppointment?.mobileNo || "",
        dob: "",
        smsPromotionalOptIn: true,
        address: "",
        doctorId: onlineAppointment?.doctorId || "",
        date: onlineAppointment?.date || "",
        startTime: startTime24,
        endTime: endTime24,
        interval: onlineAppointment?.interval || "5",
        purpose: "",
        activeTime: onlineAppointment?.activeTime || "",
        patientId: "",
      });
      setSelectedPatientId("");
      setSearchPatients([]);
      setShowPatientDropdown(false);
    }
  }, [onlineAppointment]);

  const getPurpose = useCallback(() => {
    baseUrl
      .get("client/appointment/makeappointment/findAllPurpose")
      .then((response) => {
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch]);

  useEffect(() => {
    if (purposes?.purposeIsEMpty) getPurpose();
  }, [getPurpose, purposes?.purposeIsEMpty]);

  // Search patients by phone number
  useEffect(() => {
    if (formData.mobileNo && formData.mobileNo.length >= 3) {
      setIsSearching(true);
      const searchKeyword = formData.mobileNo.replace(/\D/g, ""); // Remove non-digits
      
      baseUrl
        .get("appointment/findPatientsToAppointment", {
          headers: {
            status: true,
            keyword: searchKeyword,
          },
        })
        .then((response) => {
          setSearchPatients(response.data.data || []);
          setShowPatientDropdown(response.data.data?.length > 0);
          setIsSearching(false);
        })
        .catch((error) => {
          console.log(error);
          setSearchPatients([]);
          setShowPatientDropdown(false);
          setIsSearching(false);
        });
    } else {
      setSearchPatients([]);
      setShowPatientDropdown(false);
    }
  }, [formData.mobileNo]);

  const handlePatientSelect = (patient: any) => {
    setSelectedPatientId(patient._id);
    setformData((prevFormData: any) => ({
      ...prevFormData,
      patientId: patient._id,
      fullName: patient.fullName,
      gender: patient.gender || "",
      mobileNo: patient.mobileNo || formData.mobileNo,
      address: patient.address || "",
      title: patient.title || "",
    }));
    
    // Clear field errors for patient-related fields when patient is selected
    setFieldErrors((prev: any) => {
      const newErrors = { ...prev };
      delete newErrors.fullName;
      delete newErrors.gender;
      delete newErrors.mobileNo;
      // Clear general error if all fields are now valid
      if (Object.keys(newErrors).length === 0) {
        seterror(false);
      }
      return newErrors;
    });
    
    setShowPatientDropdown(false);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setformData((prevFormData: any) => ({ ...prevFormData, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        // Clear general error if all fields are now valid
        if (Object.keys(newErrors).length === 0) {
          seterror(false);
        }
        return newErrors;
      });
    }
    
    if (name === "mobileNo") {
      setSelectedPatientId("");
      setformData((prevFormData: any) => ({
        ...prevFormData,
        patientId: "",
      }));
    }
  };

  const handlePhonenumber = (e: any) => {
    setformData({ ...formData, mobileNo: e });
    
    // Clear field error when user starts typing
    if (fieldErrors.mobileNo) {
      setFieldErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors.mobileNo;
        // Clear general error if all fields are now valid
        if (Object.keys(newErrors).length === 0) {
          seterror(false);
        }
        return newErrors;
      });
    }
    
    setSelectedPatientId("");
    setformData((prevFormData: any) => ({
      ...prevFormData,
      patientId: "",
    }));
  };

  const handleCancel = (e: any) => {
    e.preventDefault();
    setOnlineAppointmentModal(false);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Reset field errors
    setFieldErrors({});
    seterror(false);

    // Validate required fields
    const errors: Record<string, boolean> = {};

    // Purpose is always required
    if (!formData.purpose) {
      errors.purpose = true;
    }

    let finalPatientId = formData.patientId;

    // If patient is not found, validate patient fields
    if (!finalPatientId) {
      if (!formData.fullName) {
        errors.fullName = true;
      }
      if (!formData.gender) {
        errors.gender = true;
      }
      if (!formData.mobileNo) {
        errors.mobileNo = true;
      }
    }

    // If there are validation errors, highlight fields and return
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      seterror(true);
      return;
    }

    // If patient is not found, create new patient first
    if (!finalPatientId) {
      try {
        // Create new patient
        const patientResponse = await baseUrl.post("patient/quick-register", {
          title: formData.title,
          fullName: formData.fullName,
          gender: formData.gender,
          mobileNo: formData.mobileNo,
          dob: formData.dob,
          smsPromotionalOptIn: formData.smsPromotionalOptIn,
          address: formData.address,
        });

        if (patientResponse.data.success) {
          finalPatientId = patientResponse.data.data._id;
          showSmsResultToast(patientResponse.data.sms);
          notifySmsBalanceChanged();
        } else {
          seterror(true);
          return;
        }
      } catch (error) {
        console.log(error);
        seterror(true);
        return;
      }
    }

    // Update existing appointment with patientId
    if (!onlineAppointment?.id) {
      console.error("Appointment ID not found");
      seterror(true);
      return;
    }

    const updateData = {
      doctorId: formData.doctorId,
      patientId: finalPatientId,
      date: formData.date,
      startTime: formData.startTime,
      endTime: formData.endTime,
      interval: formData.interval,
      purpose: formData.purpose,
    };

    baseUrl
      .patch("/appointment/updateonlineAppointmentById", updateData, {
        headers: {
          appointmentid: onlineAppointment.id,
          status: "appointment",
        },
      })
      .then((response) => {
        // Emit socket event for real-time update
        const dataSocket = {
          doctorId: formData.doctorId,
          patientId: {
            _id: finalPatientId,
            fullName: formData?.fullName,
          },
          activeTime: formData?.activeTime,
          date: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime,
          interval: formData.interval,
          purposeId: {
            purposeName: purposes?.purpose?.find(
              (item: any) => item._id === formData.purpose
            ).purposeName,
          },
        };
        socket.emit("newAppointment", dataSocket);
        
        onsave();
        setOnlineAppointmentModal(false);
      })
      .catch((error) => {
        console.log(error);
        seterror(true);
      });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primaryLight px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <EventIcon className="text-white" />
            </div>
            <h6 className="text-xl font-bold text-white">Add Online Appointment</h6>
          </div>
          <button
            onClick={handleCancel}
            className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <CloseIcon className="text-white" sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {isUnverifiedBooking(onlineAppointment) ? (
              <p className={`rounded-xl border px-3 py-2 text-xs font-medium ${UNVERIFIED_BOOKING_STYLE}`}>
                Unverified · {UNVERIFIED_BOOKING_HINT}
              </p>
            ) : null}
            {/* Phone Number with Search */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <PhoneIcon sx={{ fontSize: 18 }} className="text-primary" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className={`[&_.PhoneInput]:bg-slate-50 [&_.PhoneInput]:border [&_.PhoneInput]:rounded-xl [&_.PhoneInput]:px-4 [&_.PhoneInput]:py-2 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none ${
                  fieldErrors.mobileNo 
                    ? "[&_.PhoneInput]:border-red-500 [&_.PhoneInput]:bg-red-50" 
                    : "[&_.PhoneInput]:border-slate-200"
                }`}>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="LK"
                    value={formData.mobileNo}
                    onChange={handlePhonenumber}
                  />
                </div>
                {fieldErrors.mobileNo && (
                  <p className="text-sm text-red-500 mt-1">Phone number is required</p>
                )}
                
                {/* Patient Dropdown */}
                {showPatientDropdown && searchPatients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    <ul className="divide-y divide-slate-200">
                      {searchPatients.map((patient: any, i: number) => (
                        <li
                          key={i}
                          onClick={() => handlePatientSelect(patient)}
                          className={`px-4 py-3 cursor-pointer transition-all flex items-center justify-between ${
                            selectedPatientId === patient._id
                              ? "bg-indigo-50 border-l-4 border-indigo-500"
                              : "hover:bg-slate-100"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                                selectedPatientId === patient._id
                                  ? "bg-gradient-to-br from-primary to-primaryLight"
                                  : "bg-slate-300"
                              }`}
                            >
                              {patient?.fullName?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{patient?.fullName}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span>{patient?.regNo}</span>
                                {patient?.mobileNo && (
                                  <>
                                    <span>•</span>
                                    <span>{patient?.mobileNo}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          {selectedPatientId === patient._id && (
                            <CheckCircleIcon className="text-primary" sx={{ fontSize: 20 }} />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {isSearching && (
                <p className="text-xs text-slate-500">Searching patients...</p>
              )}
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <PersonIcon sx={{ fontSize: 18 }} className="text-primary" />
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="title"
                  required={true}
                  onChange={handleChange}
                  value={formData.title}
                  className={`px-4 py-2.5 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-700 ${
                    fieldErrors.fullName ? "border-red-500 bg-red-50" : "border-slate-200"
                  }`}
                >
                  <option value="">Title</option>
                  {patienttitles.map((item: any, i: number) => (
                    <option key={i} value={item.value}>
                      {item.title}
                    </option>
                  ))}
                </select>
                <div className="flex-1">
                  <input
                    type="text"
                    name="fullName"
                    onChange={handleChange}
                    value={formData.fullName}
                    placeholder="Enter full name"
                    required
                    className={`w-full px-4 py-2.5 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${
                      fieldErrors.fullName ? "border-red-500 bg-red-50" : "border-slate-200"
                    }`}
                  />
                  {fieldErrors.fullName && (
                    <p className="text-sm text-red-500 mt-1">Full name is required</p>
                  )}
                </div>
              </div>
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <WcIcon sx={{ fontSize: 18 }} className="text-primary" />
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                name="gender"
                required={true}
                onChange={handleChange}
                value={formData.gender}
                className={`w-full px-4 py-2.5 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-700 ${
                  fieldErrors.gender ? "border-red-500 bg-red-50" : "border-slate-200"
                }`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {fieldErrors.gender && (
                <p className="text-sm text-red-500 mt-1">Gender is required</p>
              )}
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <CalendarTodayIcon sx={{ fontSize: 18 }} className="text-primary" />
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            {!selectedPatientId ? (
              <PromotionalSmsOptIn
                checked={Boolean(formData.smsPromotionalOptIn)}
                onChange={(value) =>
                  setformData((prev) => ({ ...prev, smsPromotionalOptIn: value }))
                }
              />
            ) : null}

            {/* Address */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <LocationOnIcon sx={{ fontSize: 18 }} className="text-primary" />
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            {/* Purpose */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <AssignmentIcon sx={{ fontSize: 18 }} className="text-primary" />
                Purpose <span className="text-red-500">*</span>
              </label>
              <select
                name="purpose"
                required={true}
                value={formData.purpose}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-slate-700 ${
                  fieldErrors.purpose ? "border-red-500 bg-red-50" : "border-slate-200"
                }`}
              >
                <option value="">Select Purpose</option>
                {purposes?.purpose?.map((item: any, i: number) => (
                  <option key={i} value={item?._id}>
                    {item?.purposeName}
                  </option>
                ))}
              </select>
              {fieldErrors.purpose && (
                <p className="text-sm text-red-500 mt-1">Purpose is required</p>
              )}
            </div>

            {/* Dentist */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <LocalHospitalIcon sx={{ fontSize: 18 }} className="text-primary" />
                Dentist
              </label>
              <select
                name="doctorId"
                required={true}
                value={formData.doctorId}
                onChange={handleChange}
                disabled
                className="w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              >
                {doctor?.map((item: any, i: number) => (
                  <option key={i} value={item?._id}>
                    Dr. {item?.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <CalendarTodayIcon sx={{ fontSize: 18 }} className="text-primary" />
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                disabled
                className="w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              />
            </div>

            {/* Time */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <AccessTimeIcon sx={{ fontSize: 18 }} className="text-primary" />
                Time
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  disabled
                  className="flex-1 px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
                />
                <span className="text-slate-400 font-medium">to</span>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  disabled
                  className="flex-1 px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && Object.keys(fieldErrors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm font-medium">
                Please fill all required fields
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl font-medium hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-2"
          >
            <SaveIcon sx={{ fontSize: 20 }} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnlineAppointmentModal;